document.querySelectorAll('[data-product-media]').forEach((gallery) => {
  if (gallery.dataset.mediaGalleryLoaded === 'true') return;

  gallery.dataset.mediaGalleryLoaded = 'true';

  const productPage = gallery.closest('[data-product-page]');
  const viewport = gallery.querySelector('[data-media-viewport]');
  const previousButton = gallery.querySelector('[data-media-previous]');
  const nextButton = gallery.querySelector('[data-media-next]');
  const thumbnailContainer = gallery.querySelector('.product-media__thumbnails');
  const thumbnails = Array.from(gallery.querySelectorAll('[data-media-thumbnail]'));
  const slides = Array.from(gallery.querySelectorAll('[data-media-slide]'));

  if (!viewport || !slides.length) return;

  const initialSlide = slides.find((slide) => !slide.hidden);

  let activeMediaId = String(gallery.dataset.activeMediaId || initialSlide?.dataset.mediaSlide || '');
  let selectedVariantMediaId = String(gallery.dataset.selectedVariantMediaId || '');
  let scrollFrame = null;
  let pressedThumbnail = null;
  let isProgrammaticScroll = false;
  let scrollEndTimer = null;

  gallery.querySelectorAll('img').forEach((image) => {
    image.draggable = false;
  });

  gallery.addEventListener('dragstart', (event) => {
    if (event.target.closest('.product-media__thumbnail, .product-media__main-image')) {
      event.preventDefault();
    }
  });

  function clearPressedThumbnail() {
    if (pressedThumbnail) {
      pressedThumbnail.classList.remove('is-pressing');
      pressedThumbnail = null;
    }

    gallery.classList.remove('is-pointer-down');
  }

  function getSlide(mediaId) {
    return gallery.querySelector(`[data-media-slide="${String(mediaId)}"]`);
  }

  function getVisibleSlides() {
    return slides
      .filter((slide) => !slide.hidden)
      .sort((firstSlide, secondSlide) => firstSlide.offsetLeft - secondSlide.offsetLeft);
  }

  function pauseInactiveVideos() {
    slides.forEach((slide) => {
      if (slide.classList.contains('is-active')) return;

      slide.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    });
  }

  function updateThumbnailState(mediaId, shouldScroll = false) {
    const selectedMediaId = String(mediaId);

    thumbnails.forEach((thumbnail) => {
      const isSelected = thumbnail.dataset.mediaThumbnail === selectedMediaId;

      thumbnail.classList.toggle('is-active', isSelected);
      thumbnail.setAttribute('aria-current', isSelected ? 'true' : 'false');

      if (!isSelected || !shouldScroll || thumbnail.hidden || !thumbnailContainer) return;

      const thumbnailStyles = window.getComputedStyle(thumbnailContainer);
      const leftPadding = parseFloat(thumbnailStyles.paddingLeft) || 0;

      thumbnailContainer.scrollTo({
        left: Math.max(0, thumbnail.offsetLeft - leftPadding),
        behavior: 'smooth',
      });
    });
  }

  function updateArrowStates() {
    if (!previousButton || !nextButton) return;

    const visibleSlides = getVisibleSlides();
    const activeIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaSlide === activeMediaId);

    previousButton.disabled = activeIndex <= 0;
    nextButton.disabled = activeIndex === -1 || activeIndex >= visibleSlides.length - 1;
  }

  function setActiveMedia(mediaId, shouldScrollThumbnail = false) {
    if (!mediaId) return;

    activeMediaId = String(mediaId);

    slides.forEach((slide) => {
      slide.classList.toggle('is-active', slide.dataset.mediaSlide === activeMediaId);
    });

    updateThumbnailState(activeMediaId, shouldScrollThumbnail);
    pauseInactiveVideos();
    updateArrowStates();
  }

  function showCorrectVariantMedia(leadingMediaId) {
    slides.forEach((slide) => {
      const mediaId = slide.dataset.mediaSlide;
      const isVariantMedia = slide.dataset.variantMedia === 'true';
      const isSelectedVariantMedia = selectedVariantMediaId && mediaId === selectedVariantMediaId;
      const isLeadingMedia = mediaId === String(leadingMediaId);

      slide.hidden = isVariantMedia && !isSelectedVariantMedia && !isLeadingMedia;
    });
  }

  function setLeadingMedia(mediaId) {
    if (!mediaId) return;

    const leadingMediaId = String(mediaId);

    window.clearTimeout(scrollEndTimer);
    isProgrammaticScroll = false;

    showCorrectVariantMedia(leadingMediaId);

    slides.forEach((slide) => {
      const isLeading = slide.dataset.mediaSlide === leadingMediaId;
      slide.dataset.leadingMedia = isLeading ? 'true' : 'false';
    });

    requestAnimationFrame(() => {
      viewport.scrollTo({
        left: 0,
        behavior: 'auto',
      });

      setActiveMedia(leadingMediaId, false);
    });
  }

  function scrollToMedia(mediaId, behavior = 'smooth') {
    const selectedSlide = getSlide(mediaId);

    if (!selectedSlide || selectedSlide.hidden) return;

    const selectedMediaId = String(mediaId);

    window.clearTimeout(scrollEndTimer);

    /*
     * Set the clicked image active first. During smooth scrolling,
     * the scroll event must not replace it with a neighbouring slide.
     */
    isProgrammaticScroll = behavior === 'smooth';
    setActiveMedia(selectedMediaId, true);

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const targetScrollLeft = Math.min(selectedSlide.offsetLeft, maxScrollLeft);

    viewport.scrollTo({
      left: targetScrollLeft,
      behavior,
    });

    if (behavior === 'auto') {
      isProgrammaticScroll = false;
      return;
    }

    scrollEndTimer = window.setTimeout(() => {
      isProgrammaticScroll = false;
      updateActiveMediaFromScroll();
    }, 500);
  }

  function getLeftMostMediaId() {
    const visibleSlides = getVisibleSlides();

    if (!visibleSlides.length) return null;

    const currentScrollLeft = viewport.scrollLeft;
    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const scrollTolerance = 4;

    /*
     * The final slide cannot always land at offsetLeft because the
     * viewport reaches its maximum scroll position first.
     */
    if (currentScrollLeft >= maxScrollLeft - scrollTolerance) {
      return visibleSlides[visibleSlides.length - 1].dataset.mediaSlide;
    }

    let closestSlide = visibleSlides[0];
    let nearestDistance = Infinity;

    visibleSlides.forEach((slide) => {
      const distance = Math.abs(slide.offsetLeft - currentScrollLeft);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        closestSlide = slide;
      }
    });

    return closestSlide.dataset.mediaSlide;
  }

  function updateActiveMediaFromScroll() {
    const leftMostMediaId = getLeftMostMediaId();

    if (!leftMostMediaId || leftMostMediaId === activeMediaId) return;

    setActiveMedia(leftMostMediaId, false);
  }

  function goToNextMedia() {
    const visibleSlides = getVisibleSlides();
    const activeIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaSlide === activeMediaId);
    const nextSlide = visibleSlides[activeIndex + 1];

    if (!nextSlide) return;

    scrollToMedia(nextSlide.dataset.mediaSlide);
  }

  function goToPreviousMedia() {
    const visibleSlides = getVisibleSlides();
    const activeIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaSlide === activeMediaId);
    const previousSlide = visibleSlides[activeIndex - 1];

    if (!previousSlide) return;

    scrollToMedia(previousSlide.dataset.mediaSlide);
  }

  thumbnails.forEach((thumbnail) => {
    thumbnail.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || thumbnail.hidden) return;

      pressedThumbnail = thumbnail;
      thumbnail.classList.add('is-pressing');
      gallery.classList.add('is-pointer-down');
    });

    thumbnail.addEventListener('pointerup', clearPressedThumbnail);
    thumbnail.addEventListener('pointercancel', clearPressedThumbnail);
    thumbnail.addEventListener('lostpointercapture', clearPressedThumbnail);

    thumbnail.addEventListener('click', () => {
      if (thumbnail.hidden) return;

      scrollToMedia(thumbnail.dataset.mediaThumbnail);
    });
  });

  window.addEventListener('pointerup', clearPressedThumbnail, true);
  window.addEventListener('pointercancel', clearPressedThumbnail, true);
  window.addEventListener('blur', clearPressedThumbnail);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearPressedThumbnail();
    }
  });

  previousButton?.addEventListener('click', goToPreviousMedia);
  nextButton?.addEventListener('click', goToNextMedia);

  viewport.addEventListener(
    'scroll',
    () => {
      window.clearTimeout(scrollEndTimer);

      /*
       * Ignore intermediate positions created by clicking a thumbnail
       * or an arrow. This protects the intended active thumbnail.
       */
      if (isProgrammaticScroll) {
        scrollEndTimer = window.setTimeout(() => {
          isProgrammaticScroll = false;
          updateActiveMediaFromScroll();
        }, 160);

        return;
      }

      if (scrollFrame) {
        window.cancelAnimationFrame(scrollFrame);
      }

      scrollFrame = window.requestAnimationFrame(() => {
        updateActiveMediaFromScroll();
      });
    },
    { passive: true }
  );

  productPage?.addEventListener('variant:change', (event) => {
    const featuredMediaId = event.detail?.featuredMediaId || null;

    selectedVariantMediaId = featuredMediaId ? String(featuredMediaId) : '';

    const fallbackMediaId = featuredMediaId || gallery.dataset.defaultMediaId || activeMediaId;

    setLeadingMedia(fallbackMediaId);

    thumbnails.forEach((thumbnail) => {
      const isVariantMedia = thumbnail.dataset.variantMedia === 'true';

      if (!isVariantMedia) return;

      thumbnail.hidden = thumbnail.dataset.mediaThumbnail !== selectedVariantMediaId;
    });
  });

  setLeadingMedia(activeMediaId);
});